import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Button from 'components/App/Button';
import DiscoverPlaceInformation from 'components/DiscoverPlaceInformation';

import { ReactComponent as NextIconSVG } from 'assets/images/location/next-icon.svg';
import { ReactComponent as PrevIconSVG } from 'assets/images/location/prev-icon.svg';

import {
  actionCreateDiscoverPlace as actionCreateDiscoverPlaceAction,
  actionUpdateDiscoverPlace as actionUpdateDiscoverPlaceAction,
} from 'store/discover-place/actions';
import useDiscoveryCreateStore from 'store/zustand/useDiscoveryCreateStore';
import { currencyFormat } from 'utils/currencyFormat';

import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { showSuccess } from 'utils';
import styles from './AddDiscoveryConfirm.module.scss';

function AddDiscoveryConfirm(props) {
  const {
    actionCreateDiscoverPlace,
    actionUpdateDiscoverPlace,
    createLoading,
    districts,
    onBack,
    onNext,
    provinces,
    updateLoading,
    wards,
  } = props;

  const { id } = useParams();
  const { formData } = useDiscoveryCreateStore();

  const isEdit = !!id;

  const formatTime = (time24h) => {
    if (!time24h) return '';
    const [hours, minutes] = time24h.split(':');
    const hour = parseInt(hours, 10);
    const min = minutes || '00';
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min} ${period}`;
  };

  const formatServiceHours = () => {
    const from = formatTime(formData.serviceHoursFrom);
    const to = formatTime(formData.serviceHoursTo);
    return `${from} - ${to}`;
  };

  const formatAddress = () => {
    const parts = [
      formData.address,
      formData.ward,
      formData.district,
      formData.province,
    ].filter(Boolean);
    return parts.join(', ') || '';
  };

  const formatPrice = () => {
    if (!formData.priceRangeFrom) return '';
    const price = parseFloat(formData.priceRangeFrom) || 0;
    const formattedPrice = currencyFormat({
      value: price,
      currency: 'đ',
      isPrefix: false,
    });
    return `Từ ${formattedPrice}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    if (phone.startsWith('0')) {
      return `+(84)${phone.substring(1)}`;
    }
    if (phone.startsWith('+84')) {
      return phone;
    }
    return `+(84)${phone}`;
  };

  const imagesChecked = formData.images?.filter((img) => img.checked) || [];

  const mainImage = imagesChecked[0]?.image || '';

  const displayImages = mainImage
    ? imagesChecked.slice(1)
    : [];

  const formatCreatedAt = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const onConfirm = () => {
    if (createLoading) return;

    const provinceFound = provinces.find((province) =>
      province.id === +formData.province,
    );
    const districtFound = districts.find((district) =>
      district.id === +formData.district,
    );
    const wardFound = wards.find((ward) =>
      ward.id === +formData.ward,
    );

    const payload = {
      name: formData.name,
      nameEN: formData.nameEN,
      slug: formData.slug,
      previewImage: mainImage,
      images: displayImages.map((img) => img.image),
      description: formData.description,
      descriptionEN: formData.descriptionEN,
      province: provinceFound.name,
      district: districtFound.name,
      ward: wardFound.name,
      address: formData.address,
      starRating: '',
      discoverCategories: formData.discoverCategories.map((category) => category.value),
      hoursOfService: {
        from: formData.serviceHoursFrom,
        to: formData.serviceHoursTo,
      },
      priceRange: {
        from: formData.priceRangeFrom,
        // to: formData.priceRangeTo,
      },
      phoneNumber: formData.contactPhone,
      isPublished: true,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      coordinates: formData.coordinates,
      discoverProvince: formData.discoverProvince || null,
    };

    if (isEdit) {
      actionUpdateDiscoverPlace({
        id,
        payload,
        callback: () => {
          showSuccess('Chỉnh sửa địa điểm khám phá thành công');
        },
      });
    } else {
      actionCreateDiscoverPlace({
        payload,
        callback: () => {
          onNext();
        },
      });
    }
  };

  return (
    <>
      <DiscoverPlaceInformation
        name={formData.name}
        address={formatAddress()}
        description={formData.description}
        serviceHours={formatServiceHours()}
        priceRange={formatPrice()}
        phone={formatPhone(formData.contactPhone)}
        // category={formData.category || 'Chưa có hạng mục'}
        contactName={formData.contactName}
        contactEmail={formData.contactEmail}
        createdAt={formatCreatedAt()}
        mainImage={mainImage}
        images={displayImages}
      />

      <div className={styles.FormAction}>
        <Button
          classes="Btn Btn__PrevStep"
          outline
          onClick={onBack}
        >
          <PrevIconSVG className="Icon__PrevStep" />
          Quay về bước trước
        </Button>

        <Button
          classes="Btn Btn__NextStep"
          onClick={onConfirm}
          loading={createLoading || updateLoading}
          disabled={createLoading || updateLoading}
          name={isEdit ? 'Đang chỉnh sửa' : 'Đang tạo'}
        >
          {isEdit ? 'Xác nhận chỉnh sửa' : 'Xác nhận tạo'}
          {' '}
          điểm khám phá
          <NextIconSVG className="Icon__NextStep" />
        </Button>
      </div>
    </>
  );
}

AddDiscoveryConfirm.propTypes = {
  actionCreateDiscoverPlace: PropTypes.func.isRequired,
  actionUpdateDiscoverPlace: PropTypes.func.isRequired,
  createLoading: PropTypes.bool.isRequired,
  districts: PropTypes.instanceOf(Array).isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  wards: PropTypes.instanceOf(Array).isRequired,
  updateLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  createLoading: state.DiscoverPlace.createLoading,
  districts: state.Address.districts,
  provinces: state.Address.provinces,
  wards: state.Address.wards,
  updateLoading: state.DiscoverPlace.updateLoading,
});

const mapDispatchToProps = {
  actionCreateDiscoverPlace: actionCreateDiscoverPlaceAction,
  actionUpdateDiscoverPlace: actionUpdateDiscoverPlaceAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddDiscoveryConfirm);
